/**
 * Supabase GraphQL (pg_graphql / Apollo) scalar serialization — single source
 * of truth for "which fields must be cast, and how" before a mutation.
 *
 * pg_graphql exposes several Postgres types as GraphQL scalars that Apollo does
 * NOT auto-serialize from native JS values. Passing the wrong JS type throws at
 * runtime (codegen types these scalars as `any`, so there is no compile guard):
 *
 *   - BigFloat (DECIMAL/NUMERIC) → must be a string. number → "Invalid input for BigFloat type".
 *   - BigInt   (int8/bigint)     → must be a string. number → "Invalid input for BigInt type".
 *   - JSON     (jsonb)           → must be a JSON.stringify'd string. object → "Invalid input for JSON type".
 *
 * IMPORTANT — Apollo only. This applies to `client.mutate` against pg_graphql.
 * Writes through the `@supabase/supabase-js` REST client (PostgREST,
 * `.from(x).insert()`) serialize numbers and objects natively and must NOT be
 * routed through here.
 *
 * Enforced at build time by `scripts/validate-graphql-scalars.mjs`. Keep the
 * SCALAR_FIELDS registry below and that validator's field sets in sync.
 *
 * See apps/hub/.claude/rules/main-23-supabase-graphql-decimalbigfloat-serialization.md
 * and main-11-supabase-graphql-json-type-serialization.md.
 */

interface TableScalarFields {
  /** DECIMAL/NUMERIC columns — coerced with String(). */
  bigFloat?: readonly string[];
  /** int8/bigint columns — coerced with String(). */
  bigInt?: readonly string[];
  /** jsonb columns — coerced with JSON.stringify() (strings passed through). */
  json?: readonly string[];
  /**
   * timestamptz/timestamp columns exposed as the pg_graphql `Datetime` scalar.
   * The `Datetime` parser requires a full RFC 3339 timestamp; a date-only
   * `"YYYY-MM-DD"` string (what the shared calendar date editor emits) is
   * rejected with "Invalid input for Datetime type". These fields are widened
   * to a full ISO timestamp before the mutation. (A true `Date`-scalar column,
   * e.g. `deals.close_date`, must NOT be listed here — it wants date-only.)
   */
  datetime?: readonly string[];
}

/**
 * Per-table scalar field registry, derived from the generated `*InsertInput`
 * types in `src/gql/graphql.ts`. Refresh after a schema change:
 *
 *   grep -A40 'export type <Type>InsertInput = {' src/gql/graphql.ts \
 *     | grep -E 'Scalars\["(BigFloat|BigInt|JSON)"\]'
 */
export const SCALAR_FIELDS = {
  recurring_transactions: { bigFloat: ["amount", "quantity"] },
  deals: {
    bigFloat: ["cost", "estimated_hours", "margin_percentage", "monthly_amount", "revenue"],
    // `won_at` / `lost_at` are timestamptz (Datetime scalar). The detail-modal
    // calendar editor emits date-only "YYYY-MM-DD" — widen it to a full ISO
    // timestamp so pg_graphql accepts it. `close_date` is a true Date scalar
    // and is intentionally absent here. See main-23 rule.
    datetime: ["won_at", "lost_at"],
  },
  time_entries: { bigFloat: ["billable_rate", "hours"] },
  offerings: {
    bigFloat: ["base_price", "total_cost", "total_hard_costs", "total_hours"],
    json: ["hard_cost_line_items", "staff_line_items"],
  },
  tickets: {
    bigFloat: [
      "actual_hours",
      "billable_hours",
      "discount_amount",
      "discount_percentage",
      "estimated_hours",
      "hourly_rate",
      "non_billable_hours",
      "total_billed",
      "total_cost",
    ],
    // `due_date` is timestamptz (Datetime scalar). The flexpane date picker emits
    // date-only "YYYY-MM-DD" — widen it to a full ISO timestamp so pg_graphql
    // accepts it (same widening as deals `won_at`/`lost_at`). See main-23 rule.
    datetime: ["due_date"],
  },
  companies: {
    bigFloat: ["hourly_rate", "monthly_rate", "organization_revenue", "total_deal_value"],
    json: ["current_technologies", "employee_size", "social_links", "technical_identifiers"],
  },
  contacts: { json: ["custom_fields", "social_links"] },
  contracts: { json: ["metadata"] },
  social_media_posts: { json: ["results", "youtube"] },
  email_campaigns: { json: ["stats"] },
  app_users: { bigFloat: ["hourly_rate"], json: ["quickbooks_connections"] },
  revenue_targets: { bigFloat: ["target_amount"] },
  contact_proposals: { bigFloat: ["amount"], bigInt: ["file_size"] },
  contact_files: { bigInt: ["size"] },
  ticket_attachments: { bigInt: ["file_size"] },
  meridian_tool_approvals: {
    bigInt: ["expires_at", "requested_at", "responded_at"],
    json: ["parameters"],
  },
  meridian_tool_approval_overrides: { json: ["override_payload"] },
  meridian_chats: { json: ["shared_with"] },
  meridian_messages: { json: ["metadata", "parts"] },
  communications: { json: ["metadata"] },
  recurring_charges: { json: ["metadata"] },
  saved_views: { json: ["filters"] },
  automation_rules: { json: ["actions", "trigger_condition"] },
  entity_automation_config: { json: ["goal_config"] },
  notification_preferences: { json: ["reminder_config"] },
  user_preferences: { json: ["calendar_settings", "working_hours"] },
  roles: { json: ["permissions"] },
  audit_log: { json: ["details"] },
  booking_history: { json: ["attendees"] },
  calendar_cache: { json: ["event_data"] },
} as const satisfies Record<string, TableScalarFields>;

export type ScalarTable = keyof typeof SCALAR_FIELDS;

function fieldsOf(kind: keyof TableScalarFields): ReadonlySet<string> {
  const set = new Set<string>();
  for (const spec of Object.values(SCALAR_FIELDS)) {
    for (const field of (spec as TableScalarFields)[kind] ?? []) set.add(field);
  }
  return set;
}

/** Union of every BigFloat field across all tables (for table-agnostic passes). */
export const ALL_BIGFLOAT_FIELDS: ReadonlySet<string> = fieldsOf("bigFloat");
/** Union of every BigInt field across all tables. */
export const ALL_BIGINT_FIELDS: ReadonlySet<string> = fieldsOf("bigInt");
/** Union of every JSON field across all tables. */
export const ALL_JSON_FIELDS: ReadonlySet<string> = fieldsOf("json");
/** Union of every Datetime field across all tables. */
export const ALL_DATETIME_FIELDS: ReadonlySet<string> = fieldsOf("datetime");

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Datetime scalar coercion: pg_graphql's `Datetime` parser rejects a date-only
 * `"YYYY-MM-DD"` string. Widen it to a full RFC 3339 timestamp (UTC midnight);
 * already-full timestamps and null/undefined pass through untouched.
 */
function toDatetimeString(value: unknown): unknown {
  if (value === null || value === undefined) return value;
  if (typeof value === "string" && DATE_ONLY_RE.test(value.trim())) {
    return new Date(`${value.trim()}T00:00:00.000Z`).toISOString();
  }
  return value;
}

/** BigFloat/BigInt → string. null/undefined preserved (no "null", no clobber). */
function toScalarString(value: unknown): unknown {
  return value === null || value === undefined ? value : String(value);
}

/**
 * JSON → JSON.stringify. null/undefined preserved; strings passed through
 * unchanged (already serialized — avoids double-encoding).
 */
function toJsonString(value: unknown): unknown {
  if (value === null || value === undefined) return value;
  if (typeof value === "string") return value;
  return JSON.stringify(value);
}

/**
 * Coerce every pg_graphql scalar field of a mutation input to the wire type
 * pg_graphql expects. Apollo paths only (see file header). Returns a shallow
 * copy; the input is not mutated. Fields not present on the input are untouched.
 */
export function serializeGraphqlInput<T extends object>(table: ScalarTable, input: T): T {
  const spec = SCALAR_FIELDS[table] as TableScalarFields;
  const record = input as Record<string, unknown>;
  const out: Record<string, unknown> = { ...record };

  for (const field of spec.bigFloat ?? []) {
    if (field in record) out[field] = toScalarString(record[field]);
  }
  for (const field of spec.bigInt ?? []) {
    if (field in record) out[field] = toScalarString(record[field]);
  }
  for (const field of spec.json ?? []) {
    if (field in record) out[field] = toJsonString(record[field]);
  }
  for (const field of spec.datetime ?? []) {
    if (field in record) out[field] = toDatetimeString(record[field]);
  }

  return out as T;
}

/**
 * Table-agnostic Datetime widening for inputs assembled outside a single
 * table's serializer (e.g. the universal inline-edit path). Widens any field in
 * `ALL_DATETIME_FIELDS` whose value is a date-only string to a full ISO
 * timestamp. Shallow copy; input is not mutated.
 */
export function normalizeGraphqlDatetimeInput<T extends object>(input: T): T {
  const record = input as Record<string, unknown>;
  const out: Record<string, unknown> = { ...record };
  for (const field of ALL_DATETIME_FIELDS) {
    if (field in record) out[field] = toDatetimeString(record[field]);
  }
  return out as T;
}
